"use client";

import { Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  question: "What materials do you specialize in engraving?",
  answer: "We specialize in high-precision engraving on industrial-grade materials, including phenolic, stainless steel, aluminum, and various plastics. Our advanced laser systems ensure clean, permanent markings that meet rigorous industry standards for durability and legibility."
}, {
  question: "Do you provide UL 508A compliant labels?",
  answer: "Yes. We are fully equipped to produce UL 508A compliant labels for panel builders. Our team understands the strict requirements for material certification and permanence, ensuring your equipment meets all necessary safety and regulatory codes."
}, {
  question: "What is your typical turnaround time for orders?",
  answer: "We pride ourselves on efficiency. Standard orders are typically processed and shipped within 3 to 5 business days. For urgent projects or large-scale submittal packages, we offer expedited services to keep your production schedule on track."
}, {
  question: "Can you handle custom design files?",
  answer: "Absolutely. We accept most standard vector file formats, including DXF, DWG, and AI. If you only have a sketch or a physical sample, our design team can digitize your requirements to ensure the final product matches your exact specifications."
}, {
  question: "Do you offer bulk pricing for large projects?",
  answer: "Yes, we provide competitive volume pricing for large-scale industrial projects and recurring orders. Contact our sales team with your project details, and we will provide a tailored quote that reflects the scale of your requirements."
}, {
  question: "What makes your engraving process superior?",
  answer: "Our process combines state-of-the-art fiber and CO2 laser technology with decades of industrial expertise. Unlike traditional stamping, our laser engraving provides unmatched precision, zero mechanical stress on the material, and permanent results that withstand harsh environments."
}, {
  question: "Are your labels suitable for outdoor environments?",
  answer: "Yes. We select UV-stable and weather-resistant materials specifically for outdoor applications. Whether for power utilities or marine environments, our engravings are designed to resist fading, corrosion, and extreme temperature fluctuations."
}, {
  question: "How do I request a quote for a submittal package?",
  answer: "You can request a quote by visiting our contact page or uploading your project specifications directly through our portal. We will review your requirements and provide a comprehensive submittal package including material data sheets and proofs."
}];
export default function FaqAccordion() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2463" id="faq-accordion" className="bg-background text-foreground py-24 md:py-32 lg:py-40 px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="lg:sticky lg:top-24">
              <div className="font-mono text-xs tracking-widest uppercase text-primary mb-8">
                INDUSTRIAL INSIGHTS / 03
              </div>

              <h2 className="font-default font-bold text-accent mb-10" style={{
              fontSize: "clamp(2.75rem, 5vw, 5rem)",
              lineHeight: "0.95",
              letterSpacing: "-0.03em"
            }}>
                Common
                <br />
                Technical
                <br />
                Queries.
              </h2>

              <p className="font-default text-base md:text-lg leading-relaxed mb-10" style={{
              maxWidth: "360px",
              color: "var(--foreground)"
            }}>
                We have compiled the most frequent questions regarding our industrial engraving services. If you need further clarification, our team is ready to assist.
              </p>


            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 ease-out delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Accordion type="single" collapsible defaultValue="item-0" className="flex flex-col gap-3">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-0 backdrop-blur-sm transition-all duration-300 group rounded-lg" style={{
              backgroundColor: "var(--card)",
              border: "1px solid var(--border)",
              boxShadow: "var(--shadow-md)"
            }}>
                  <AccordionTrigger className="hover:no-underline px-6 md:px-8 py-6 md:py-7 min-h-[96px] gap-6 [&>svg]:hidden group-hover:[border-color:var(--primary)]">
                    <div className="flex items-center gap-6 md:gap-8 flex-1 text-left">
                      <span className="font-default font-bold text-xs text-primary shrink-0 tracking-wider">
                        Q.{String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-default font-medium text-base md:text-lg text-accent leading-snug">
                        {faq.question}
                      </span>
                    </div>
                    <Plus className="size-6 text-primary shrink-0 transition-transform duration-300 group-data-[state=open]:rotate-45" strokeWidth={1.5} />
                  </AccordionTrigger>
                  <AccordionContent className="px-6 md:px-8 pb-8 pt-0">
                    <div className="pl-0 md:pl-[88px]">
                      <p className="font-default text-base md:text-[1.0625rem]" style={{
                    lineHeight: "1.75",
                    maxWidth: "600px",
                    color: "var(--foreground)"
                  }}>
                        {faq.answer}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}